import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Typography,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import BackLink from "../shared/BackLink";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import {
  fetchAllModules,
  filterRelevantModules,
} from "../shared/functions/getUniversityStatus";
import ProgressTracking from "./ProgressTracking";
import FilterBar from "./FilterBar";
import { TabbedNavigation, TabPanel } from "./TabbedNavigation";
import ModuleCard from "./ModuleCard";
import {
  formatModulesForDisplay,
  generateCategoryOptions,
  formatSupplierOptions,
} from "./trainingUtils";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.75rem",
      marginBottom: theme.spacing(2),
    },
  },
  modulesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr", // Single column on mobile
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(4),
    },
  },
  filtersSection: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  paginationContainer: {
    marginTop: theme.spacing(4),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(3),
      justifyContent: "center",
      "& > *": {
        margin: theme.spacing(1, 0),
      },
    },
  },
  pagination: {
    [theme.breakpoints.down("xs")]: {
      order: 2,
    },
  },
  pageSize: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      order: 1,
      width: "100%",
      justifyContent: "center",
      marginBottom: theme.spacing(2),
    },
  },
  pageSizeLabel: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  pageSizeSelect: {
    minWidth: 80,
  },
  resultsInfo: {
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      order: 3,
      marginTop: theme.spacing(1),
      textAlign: "center",
      width: "100%",
    },
  },
}));

/**
 * TrainingContentViewer component
 * that presents training modules in a more user-friendly way.
 */
function TrainingContentViewer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles();
  const { currentUser } = useAuth();
  const { activeSuppliers, productTypes } = useCompany();

  // State variables
  const [loadingPage, setLoadingPage] = useState(true);
  const [trainingModules, setTrainingModules] = useState([]);
  const [displayModules, setDisplayModules] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(isMobile ? 4 : 8);
  const [totalPages, setTotalPages] = useState(0);
  const [paginatedModules, setPaginatedModules] = useState([]);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Filter options
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);

  // Load modules on mount
  useEffect(() => {
    fetchModules();
  }, [activeSuppliers]);

  // Update displayed modules when filters or tabs change
  useEffect(() => {
    if (trainingModules.length > 0) {
      applyFilters();
    }
  }, [
    searchQuery,
    statusFilter,
    categoryFilter,
    supplierFilter,
    activeTab,
    trainingModules,
  ]);

  // Handle page size changes on mobile/desktop switch
  useEffect(() => {
    if (isMobile && pageSize > 4) {
      setPageSize(4);
      setPage(1); // Reset to first page when switching to mobile
    }
  }, [isMobile, pageSize]);

  // Update paginated modules when displayModules, page, or pageSize changes
  useEffect(() => {
    paginateModules();
  }, [displayModules, page, pageSize]);

  // Fetch modules
  const fetchModules = async () => {
    try {
      setLoadingPage(true);

      const { data } = await fetchAllModules(currentUser.id);

      if (!data || data.length === 0) {
        setLoadingPage(false);
        return;
      }

      // Filter to relevant modules
      const relevantModules = filterRelevantModules(
        currentUser,
        activeSuppliers,
        productTypes,
        data
      );

      // Format modules for display
      const formattedModules = formatModulesForDisplay(relevantModules);

      // Set state
      setTrainingModules(formattedModules);
      setDisplayModules(formattedModules);

      // Set filter options
      setCategoryOptions(generateCategoryOptions(formattedModules));
      setSupplierOptions(formatSupplierOptions(activeSuppliers));

      setLoadingPage(false);
    } catch (error) {
      console.error("Error fetching modules:", error);
      setLoadingPage(false);
    }
  };

  // Apply filters based on current selections
  const applyFilters = () => {
    let filtered = [...trainingModules];

    // Apply tab filter
    switch (activeTab) {
      case 1: // Required
        filtered = filtered.filter(module => module.is_compulsory);
        break;
      case 2: // Completed
        filtered = filtered.filter(module => module.status.passed);
        break;
      default:
      // All modules (no filter)
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        module =>
          module.module_name.toLowerCase().includes(query) ||
          (module.module_description &&
            module.module_description.toLowerCase().includes(query)) ||
          (module.module_category &&
            module.module_category.toLowerCase().includes(query))
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(module => {
        switch (statusFilter) {
          case "passed":
            return module.status.passed;
          case "failed":
            return module.status.failed;
          case "not-started":
            return module.status.notStarted;
          case "required":
            return module.is_compulsory;
          default:
            return true;
        }
      });
    }

    // Apply category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        module => module.module_category === categoryFilter
      );
    }

    // Apply supplier filter
    if (supplierFilter !== "all") {
      filtered = filtered.filter(module =>
        module.module_suppliers.includes(supplierFilter)
      );
    }

    setDisplayModules(filtered);
    // Reset to first page when filters change
    setPage(1);
  };

  // Paginate the filtered modules
  const paginateModules = () => {
    // Calculate total pages
    const totalsPages = Math.ceil(displayModules.length / pageSize);
    setTotalPages(totalsPages);

    // Get current page modules
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, displayModules.length);
    const currentPageModules = displayModules.slice(startIndex, endIndex);

    setPaginatedModules(currentPageModules);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top of module grid
    window.scrollTo({
      top: document.getElementById("modules-grid").offsetTop - 20,
      behavior: "smooth",
    });
  };

  // Handle page size change
  const handlePageSizeChange = event => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Render empty state
  const renderEmptyState = message => (
    <div className={classes.emptyState}>
      <Typography variant="h6">
        {message || "No modules match your current filters."}
      </Typography>
      <Typography variant="body2">
        Try adjusting your search criteria or filter settings.
      </Typography>
    </div>
  );

  // Render modules grid
  const renderModulesGrid = modules => (
    <div id="modules-grid" className={classes.modulesGrid}>
      {modules.map(module => (
        <ModuleCard key={module.id} module={module} />
      ))}
    </div>
  );

  // Render pagination controls
  const renderPagination = () => {
    if (displayModules.length === 0) return null;

    const startItem = (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, displayModules.length);

    return (
      <div className={classes.paginationContainer}>
        <div className={classes.pageSize}>
          <Typography variant="body2" className={classes.pageSizeLabel}>
            Modules per page:
          </Typography>
          <FormControl
            size="small"
            variant="outlined"
            className={classes.pageSizeSelect}
          >
            <Select value={pageSize} onChange={handlePageSizeChange}>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={16}>16</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Pagination
          className={classes.pagination}
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />

        <Typography variant="body2" className={classes.resultsInfo}>
          {startItem}-{endItem} of {displayModules.length} modules
        </Typography>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <BackLink />

      <Typography variant="h4" className={classes.title}>
        Zawadi University Training
      </Typography>

      {loadingPage ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          {/* Progress Tracking */}
          <ProgressTracking modules={trainingModules} />

          {/* Filters */}
          <div className={classes.filtersSection}>
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={setCategoryFilter}
              supplierFilter={supplierFilter}
              onSupplierFilterChange={setSupplierFilter}
              categories={categoryOptions}
              suppliers={supplierOptions}
            />
          </div>

          {/* Tabbed Navigation */}
          <TabbedNavigation activeTab={activeTab} onChange={setActiveTab} />

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            {displayModules.length > 0 ? (
              <>
                {renderModulesGrid(paginatedModules)}
                {renderPagination()}
              </>
            ) : (
              renderEmptyState("No modules found.")
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {displayModules.length > 0 ? (
              <>
                {renderModulesGrid(paginatedModules)}
                {renderPagination()}
              </>
            ) : (
              renderEmptyState("No required modules found.")
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {displayModules.length > 0 ? (
              <>
                {renderModulesGrid(paginatedModules)}
                {renderPagination()}
              </>
            ) : (
              renderEmptyState("No completed modules found.")
            )}
          </TabPanel>
        </>
      )}
    </div>
  );
}

export default TrainingContentViewer;
